import type { ReactNode } from 'react'
import { cn } from '@/utilities/ui'

export const FOCUS_VISIBLE_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-bright-blue'
export const ROW_HOVER_CLASS = 'hover:bg-cp-light-purple/70'
export const SEARCH_FOCUS_CLASS =
  'focus-within:border-cp-bright-blue focus-within:[outline-style:solid] focus-within:outline-1 focus-within:outline-offset-0 focus-within:outline-cp-bright-blue'

export const EMPTY_SCROLL_AFFORDANCE = {
  canScrollDown: false,
  canScrollUp: false,
}

export type ScrollAffordance = typeof EMPTY_SCROLL_AFFORDANCE

export function IconTile({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-light-grey bg-white">
      {children}
    </div>
  )
}

export function MatchBadge({ count }: Readonly<{ count: number }>) {
  if (count <= 0) return null
  return (
    <span
      className="rounded bg-cp-beta-bg px-1.5 py-0.5 text-xs leading-tight text-cp-beta-text whitespace-nowrap"
      data-testid="org-program-switcher-match-badge"
    >
      {count} match{count === 1 ? '' : 'es'}
    </span>
  )
}

export function SelectedBadge() {
  return (
    <span className="rounded bg-white px-1.5 py-0.5 text-[11px] leading-tight font-medium text-cp-bright-blue">
      Selected
    </span>
  )
}

export function ScrollFade({
  edge,
  testId,
  visible,
}: Readonly<{ edge: 'top' | 'bottom'; testId: string; visible: boolean }>) {
  return visible ? (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-x-0 h-6 from-white via-white/90 to-white/0',
        edge === 'top' ? 'top-0 bg-gradient-to-b' : 'bottom-0 bg-gradient-to-t',
      )}
      data-testid={testId}
    />
  ) : null
}

export function getScrollAffordance(element: HTMLDivElement | null): ScrollAffordance {
  if (element) {
    const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight)
    return {
      canScrollDown: element.scrollTop < maxScrollTop - 1,
      canScrollUp: element.scrollTop > 1,
    }
  }
  return EMPTY_SCROLL_AFFORDANCE
}
